<script setup lang="ts">
  import { Check, History, Plus, X } from "lucide-vue-next";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import { Tooltip } from "@/components/ui/tooltip";
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
  } from "@/components/ui/dropdown-menu";

  interface ConversationSummary {
    token: string;
    title: string;
  }

  withDefaults(
    defineProps<{
      working?: boolean;
      conversationTitle?: string;
      conversations?: ConversationSummary[];
      currentConversationToken?: string | null;
      showHistory?: boolean;
      closable?: boolean;
    }>(),
    {
      working: false,
      conversationTitle: "New conversation",
      conversations: () => [],
      currentConversationToken: null,
      showHistory: true,
      closable: true,
    },
  );

  const emit = defineEmits(["close", "new", "select-conversation"]);
</script>

<template>
  <header class="relative flex shrink-0 items-center gap-2 border-b border-border py-2.5 pl-3.5 pr-2.5">
    <div class="flex min-w-0 items-center gap-2.5">
      <AiStar class="size-5 shrink-0 text-ai-text" />
      <div class="flex min-w-0 flex-col gap-0.5">
        <div class="flex items-center gap-2 text-13 font-medium leading-tight">
          <span class="whitespace-nowrap">Episto AI</span>
          <Badge
            variant="success"
            class="rounded px-1.5 text-3xs">
            beta
          </Badge>
        </div>
        <span class="truncate text-2xs leading-tight text-muted-foreground">{{ conversationTitle }}</span>
      </div>
    </div>

    <div class="ml-auto flex shrink-0 items-center gap-0.5">
      <DropdownMenu v-if="showHistory">
        <DropdownMenuTrigger as-child>
          <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7 text-muted-foreground"
            :disabled="working"
            title="History">
            <History class="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          class="max-h-80 w-72 overflow-y-auto">
          <DropdownMenuLabel class="text-xs font-normal text-muted-foreground">
            Recent conversations on this page
          </DropdownMenuLabel>
          <DropdownMenuItem
            v-for="conversation in conversations"
            :key="conversation.token"
            class="gap-2 text-xs"
            @select="emit('select-conversation', conversation.token)">
            <Check
              class="h-3.5 w-3.5 shrink-0"
              :class="conversation.token === currentConversationToken ? 'opacity-100' : 'opacity-0'" />
            <span class="flex-1 truncate">{{ conversation.title }}</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            v-if="!conversations.length"
            disabled
            class="text-xs">
            No conversation yet
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Tooltip content="New conversation">
        <Button
          variant="ghost"
          size="icon"
          class="h-7 w-7 text-muted-foreground"
          :disabled="working"
          @click="emit('new')">
          <Plus class="h-4 w-4" />
        </Button>
      </Tooltip>
      <Tooltip
        v-if="closable"
        content="Close">
        <Button
          variant="ghost"
          size="icon"
          class="h-7 w-7 text-muted-foreground"
          @click="emit('close')">
          <X class="h-4 w-4" />
        </Button>
      </Tooltip>
    </div>

    <span
      class="ai-aurora absolute inset-x-0 -bottom-px h-0.5 transition-opacity duration-300"
      :class="working ? 'opacity-100' : 'opacity-0'" />
  </header>
</template>
